/**
 * The one place App changes the visible stage, in two flavours.
 *
 * Both do the same visual transition and both advance the broad
 * `navigationIntentEpochRef` (a pending Stage 3 Next or re-quote extraction
 * compares it; that contract is unchanged). They differ in one thing:
 *
 *   - `setStageWithTransition` is the OPERATOR's stage change. Back, Next, the
 *     stage pills, the assistant's stage request, the result ribbon and the
 *     scope-issue jump all come through here. It also advances
 *     `operatorNavigationEpochRef`, the accepted-operator-navigation receipt.
 *   - `setStageWithTransitionAutomatic` is the PIPELINE's own advance: Risk
 *     painting Stage 2, handing off to Stage 3, Work Method advancing to
 *     Stage 4. It must not look like the operator moving, or a run could never
 *     compare the receipt: its own transition would cancel itself.
 *
 * Only the automatic Risk and Work Method hooks are given the automatic
 * flavour. Everything else, including any control a future change adds, gets
 * the operator flavour by default, so an unknown caller errs toward honouring
 * the operator and never toward overriding them.
 */
import type { MutableRefObject } from 'react';

export interface StageChangerDeps {
    /**
     * The stage the screen is ACTUALLY showing, read live (5C / C26). It must not be a value captured by the render that built the changer: a
     * long Risk or Work Method run keeps the changer it started with, and a stale stage makes a later request for the now-current stage's
     * successor look like a no-op (a run begun on Stage 3 paints 2, then asks for 3, and is skipped). The caller keeps this current both when it
     * renders and when the changer's own `setStage` paints.
     */
    getStage: () => number;
    navigationIntentEpochRef: MutableRefObject<number>;
    operatorNavigationEpochRef: MutableRefObject<number>;
    setStageDirection: (direction: 'forward' | 'back') => void;
    setIsStageTransitioning: (transitioning: boolean) => void;
    setPrevStage: (stage: number) => void;
    setStage: (stage: number) => void;
    /** setTimeout by default; injectable so tests can flush it deterministically. */
    schedule?: (callback: () => void, milliseconds: number) => unknown;
}

export function createStageChanger(deps: StageChangerDeps) {
    const change = (newStage: number, byOperator: boolean) => {
        const stage = deps.getStage();
        if (newStage === stage) return;
        deps.navigationIntentEpochRef.current += 1;
        if (byOperator) deps.operatorNavigationEpochRef.current += 1;
        deps.setStageDirection(newStage > stage ? 'forward' : 'back');
        deps.setIsStageTransitioning(true);
        deps.setPrevStage(stage);
        deps.setStage(newStage);
        (deps.schedule || ((callback, ms) => setTimeout(callback, ms)))(() => deps.setIsStageTransitioning(false), 350);
    };
    return {
        setStageWithTransition: (newStage: number) => change(newStage, true),
        setStageWithTransitionAutomatic: (newStage: number) => change(newStage, false),
    };
}
